import { GeocodeResponse } from '@/types/MapboxApi'
import { queryOptions, useQuery } from '@eigtech/ui-shared-api'
import { DelegateAdminClaim } from '@eigtech/ui-shared-claims-lib'
import { mapboxPlacesBasePath } from '../constants'
import { mapboxInstance } from '../instance'
import { claimsQueryKeys } from './constants'

const getLocationFromMapbox = (address: string) =>
  mapboxInstance.get<GeocodeResponse>(`${mapboxPlacesBasePath}/${address}.json`, {
    searchParams: {
      autocomplete: false,
      limit: 1,
      countries: JSON.stringify(['US']),
    },
  })

export const getLocationFromMapboxQueryOptions = ({
  address,
  claimNumber,
}: {
  claimNumber: string
  address: string | undefined
}) =>
  queryOptions({
    queryKey: claimsQueryKeys.mapboxLocation(claimNumber),
    queryFn: () => getLocationFromMapbox(address ?? ''),
    enabled: !!address,
  })

export const useGetLocationFromMapbox = (props: {
  claimNumber: string
  address: string | undefined
}) => useQuery(getLocationFromMapboxQueryOptions(props))

export function useGetLatLongForClaim({
  claimNumber,
  location,
}: {
  claimNumber: string
  location: DelegateAdminClaim['lossLocation'] | undefined
}) {
  let address: string | undefined = undefined

  if (location) {
    address = [
      location.address.addressLine1,
      location.address.addressLine2,
      location.address.addressLine3,
      location.address.city,
      location.address.countyOrParishCode,
      location.address.stateOrProvince,
      location.address.zipOrPostalCode,
    ]
      .filter((part): part is string => !!part)
      .join(', ')
  }

  return useQuery(
    queryOptions({
      queryKey: claimsQueryKeys.mapboxLocation(claimNumber),
      queryFn: async () => {
        if (location?.geoCoordinates?.latitude) {
          return {
            latitude: location.geoCoordinates.latitude,
            longitude: location.geoCoordinates.longitude,
          }
        }

        const { features } = await getLocationFromMapbox(address ?? '')
        if (features[0]) {
          const [longitude, latitude] = features[0].geometry.coordinates
          return { latitude, longitude }
        }
      },
      enabled: !!location,
    })
  )
}
